import {
  Button,
  Inline,
  Stack,
  Text,
  Box,
  IconAI,
  TextLinkButton,
  useResponsiveValue,
  useToast,
  Heading,
} from 'braid-design-system';
import { useState, useMemo } from 'react';

import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useSelector } from 'src/store/react';
import { useRunSearch } from 'src/store/search/useRunSearch';
import { selectSearchQuery } from 'src/store/selectors';

import { NudgePill } from './NudgePill';
import { MAX_VISIBLE_PILLS_COLLAPSED, MAX_DISPLAY_PILLS } from './constants';

// TODO: Remove this to use Facet types from @seek/chalice-types - DJS-1600
interface FacetTypesDataProp {
  id: string;
  count: number;
  label: string;
}

interface NudgeIntentProps {
  facetTypesData: FacetTypesDataProp[];
  isMultiSelect?: boolean;
  title: string;
  description?: string;
  displayToastMessage: string;
}

export const NudgeIntent = ({
  facetTypesData,
  isMultiSelect = true,
  title,
  description,
  displayToastMessage,
}: // TODO: Hardcoded for multi-select to true for now. - DJS-1600
// Sort out the type of facets in parent component, SearchResultList
// whether it is multi-select or single-select.
// Multi-select -> workType and workArrangement
// Single-select -> salaryMin & distintTitle
NudgeIntentProps) => {
  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) || false;

  const showToast = useToast();
  const searchQuery = getRefineSearchQuery(useSelector(selectSearchQuery));
  const runSearch = useRunSearch();

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(),
  );
  const [expanded, setExpanded] = useState(false);

  const maximumNudgePillsToShow = facetTypesData.slice(0, MAX_DISPLAY_PILLS);
  const showMoreOrLessPillsButton =
    maximumNudgePillsToShow.length > MAX_VISIBLE_PILLS_COLLAPSED;

  const nudgePillsToDisplay = expanded
    ? maximumNudgePillsToShow
    : maximumNudgePillsToShow.slice(0, MAX_VISIBLE_PILLS_COLLAPSED);

  const numberOfHiddenPills =
    maximumNudgePillsToShow.length - MAX_VISIBLE_PILLS_COLLAPSED;

  const nudgePillsWithActiveState = useMemo(
    () =>
      nudgePillsToDisplay?.map((pill, index) => ({
        ...pill,
        isActive: selectedIndices.has(index),
        index,
      })),
    [nudgePillsToDisplay, selectedIndices],
  );

  const handleNudgePillClick = (pillIndex: number) => {
    setSelectedIndices((previousSelection) => {
      const wasSelected = previousSelection.has(pillIndex);

      if (isMultiSelect) {
        const newSet = new Set(previousSelection);
        if (wasSelected) {
          newSet.delete(pillIndex);
        } else {
          newSet.add(pillIndex);
        }
        return newSet;
      }
      // Single-select
      return wasSelected ? new Set() : new Set([pillIndex]);
    });
  };

  const nudgePills = nudgePillsWithActiveState?.map(
    ({ label, count, isActive, index }) => (
      <NudgePill
        key={index}
        label={label}
        count={count}
        isActive={isActive}
        index={index}
        isMobile={isMobile}
        onClick={handleNudgePillClick}
      />
    ),
  );

  const selectedJobsCount = useMemo(
    () =>
      Array.from(selectedIndices).reduce(
        (sum, index) => sum + (maximumNudgePillsToShow[index]?.count || 0),
        0,
      ),
    [selectedIndices, maximumNudgePillsToShow],
  );

  return (
    <Box padding="medium" boxShadow="borderPromoteLight" borderRadius="large">
      <Stack space="medium">
        <Stack space="small">
          <Inline space="none" alignY="center">
            <Heading level="4" weight="regular">
              <IconAI tone="promote" />
            </Heading>
            <Text
              weight="strong"
              tone="promote"
              size={isMobile ? 'small' : 'standard'}
            >
              {title}
            </Text>
          </Inline>
          <Text size={isMobile ? 'small' : 'standard'}>{description}</Text>
        </Stack>
        <Inline space={{ mobile: 'xxsmall', tablet: 'xsmall' }} alignY="center">
          {nudgePills}
          {showMoreOrLessPillsButton && (
            <Box
              data-automation="showMoreOrLessPillsButton"
              component="button"
              onClick={() => setExpanded(!expanded)}
            >
              <Text size="small">
                <TextLinkButton weight="weak">
                  {expanded ? 'Show less' : `+${numberOfHiddenPills} more`}
                </TextLinkButton>
              </Text>
            </Box>
          )}
        </Inline>
        {selectedIndices.size > 0 && (
          <Button
            data-automation="selectedJobsCountButton"
            onClick={() => {
              runSearch({
                pathname: searchQuery,
              });
              showToast({
                message: displayToastMessage,
                tone: 'positive',
              });
            }}
            variant="solid"
            tone="brandAccent"
          >
            SEEK {selectedJobsCount} Jobs
          </Button>
        )}
      </Stack>
    </Box>
  );
};
